/**
 * THE TASK QUEUE — what the playground does with a message it cannot run yet.
 *
 * One project has one working tree, and two agent turns editing the same tree
 * at once corrupt each other's work (the reason Codex and Claude Code put
 * parallel agents in separate worktrees). So a tab runs ONE turn at a time,
 * and everything else waits here, VISIBLY, in the order it arrived:
 *
 *  - `waiting`     — typed while another turn was running (in this session or
 *                    another one). It runs in ITS OWN session, never steered
 *                    into whichever session happens to be streaming.
 *  - `deferred`    — the agent judged a mid-turn message to be a separate task
 *                    (`turn.defer`) and set it aside; it runs next.
 *  - `undelivered` — accepted mid-turn but the turn closed before the model
 *                    saw it; handed back by the loop and run next.
 *
 * Every item can be removed or moved up. A turn that is STOPPED or FAILS
 * pauses the queue rather than marching on into the next task — a person who
 * pressed Stop did not ask for the next thing to start. The queue survives a
 * reload (localStorage) and comes back paused, for the same reason.
 *
 * Pure apart from the injected storage, so it is tested without a DOM.
 */
export type QueueReason = "waiting" | "deferred" | "undelivered";

export interface QueuedTask {
  readonly id: string;
  readonly sessionId: string;
  readonly text: string;
  readonly reason: QueueReason;
}

export interface QueueStorage {
  load(): unknown;
  save(value: unknown): void;
}

const MAX_ITEMS = 50;
const MAX_TEXT = 8_000;

function isTask(v: unknown): v is QueuedTask {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return typeof r["id"] === "string" && typeof r["sessionId"] === "string" && typeof r["text"] === "string" && (r["reason"] === "waiting" || r["reason"] === "deferred" || r["reason"] === "undelivered");
}

export class TaskQueue {
  private list: QueuedTask[] = [];
  private seq = 0;
  /** Stopped or failed last time — nothing runs until the user resumes. */
  paused = false;

  constructor(private readonly storage?: QueueStorage) {
    try {
      const raw = storage?.load() as { items?: unknown } | null | undefined;
      const items = Array.isArray(raw?.items) ? raw.items.filter(isTask).slice(0, MAX_ITEMS) : [];
      this.list = items;
      // Restored work never starts by itself: the user left with it pending.
      this.paused = items.length > 0;
    } catch {
      this.list = [];
    }
  }

  get items(): readonly QueuedTask[] {
    return this.list;
  }

  get size(): number {
    return this.list.length;
  }

  forSession(sessionId: string): QueuedTask[] {
    return this.list.filter((t) => t.sessionId === sessionId);
  }

  /** Add to the back (waiting) — or the FRONT, in order, for work handed back by a finished turn. */
  enqueue(sessionId: string, texts: string | readonly string[], reason: QueueReason, front = false): QueuedTask[] {
    const added: QueuedTask[] = [];
    for (const raw of typeof texts === "string" ? [texts] : texts) {
      const text = raw.trim().slice(0, MAX_TEXT);
      if (text === "") continue;
      this.seq += 1;
      added.push({ id: `q${String(Date.now())}-${String(this.seq)}`, sessionId, text, reason });
    }
    if (added.length === 0) return [];
    this.list = front ? [...added, ...this.list] : [...this.list, ...added];
    // Past the cap the overflow is refused, not silently dropped later: the
    // return value lists only what was actually queued, so the caller can say so.
    if (this.list.length > MAX_ITEMS) this.list = this.list.slice(0, MAX_ITEMS);
    this.persist();
    return added.filter((a) => this.list.includes(a));
  }

  remove(id: string): void {
    this.list = this.list.filter((t) => t.id !== id);
    if (this.list.length === 0) this.paused = false;
    this.persist();
  }

  moveUp(id: string): void {
    const i = this.list.findIndex((t) => t.id === id);
    if (i <= 0) return;
    const next = [...this.list];
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    this.list = next;
    this.persist();
  }

  clear(): void {
    this.list = [];
    this.paused = false;
    this.persist();
  }

  pause(): void {
    if (this.list.length > 0) this.paused = true;
  }

  resume(): void {
    this.paused = false;
  }

  /** Take the next runnable task (FIFO), or nothing while paused. */
  take(): QueuedTask | undefined {
    if (this.paused) return undefined;
    const next = this.list[0];
    if (!next) return undefined;
    this.list = this.list.slice(1);
    this.persist();
    return next;
  }

  private persist(): void {
    try {
      this.storage?.save({ items: this.list });
    } catch {
      /* storage is a convenience; the in-memory queue is the truth */
    }
  }
}

/** One-line label for a queued item's reason, shown on its row. */
export function reasonLabel(reason: QueueReason): string {
  switch (reason) {
    case "deferred":
      return "Next task — set aside by the agent";
    case "undelivered":
      return "Arrived as the last turn ended";
    default:
      return "Waiting";
  }
}

/** localStorage-backed storage; any failure (private mode, quota) degrades to memory-only. */
export function localQueueStorage(key: string): QueueStorage {
  return {
    load: () => {
      try {
        const raw = globalThis.localStorage?.getItem(key);
        return raw ? (JSON.parse(raw) as unknown) : null;
      } catch {
        return null;
      }
    },
    save: (value) => {
      try {
        globalThis.localStorage?.setItem(key, JSON.stringify(value));
      } catch {
        /* memory-only */
      }
    },
  };
}
